<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';

import { useVisitasStore } from 'dashboard/stores/visitas';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';

import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisInfoRow from 'dashboard/components-next/LaisInfoRow.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import HouseContactsList from '../components/HouseContactsList.vue';
import OccupiedHouseNotice from '../components/OccupiedHouseNotice.vue';
import VisitHistorySummary from '../components/VisitHistorySummary.vue';
import {
  VISIT_CANCELLATION_NOTICE,
  VISIT_CANCEL_CONFIRM_LABEL,
  VISIT_CANCEL_CONFIRM_TITLE,
  VISIT_CANCEL_DETAILS_TITLE,
  VISIT_CANCEL_INVALID_DATE_MESSAGE,
  VISIT_CANCEL_INVALID_FIRST_DAY_MESSAGE,
  VISIT_CANCEL_REASON_TITLE,
  VISIT_CANCEL_RESCHEDULE_LINK,
  VISIT_CANCEL_RESCHEDULE_TEXT,
  VISIT_CANCEL_RESPONSIBLE_OPTIONS,
  VISIT_CANCEL_SUSPEND_BODY,
  VISIT_CANCEL_SUSPEND_CONFIRM_LABEL,
  VISIT_CANCEL_SUSPEND_FLOW_BODY,
  VISIT_CANCEL_SUSPEND_FLOW_TITLE,
  VISIT_CANCEL_SUSPEND_HELPER,
  VISIT_CANCEL_SUSPEND_LABEL,
  VISIT_CANCEL_SUSPEND_LAST_DAY_LABEL,
  VISIT_CANCEL_SUSPEND_PERIOD_LABEL,
  VISIT_CANCEL_SUSPEND_SUCCESS_MESSAGE,
  VISIT_CANCEL_SUSPEND_SUCCESS_TITLE,
  VISIT_CANCEL_SUSPEND_TITLE,
  VISIT_CANCEL_SUCCESS_MESSAGE,
  VISIT_CANCEL_SUCCESS_TITLE,
  cancelReasonLabel,
  cancelResponsibleLabel,
  doormanLabel,
  entryAccessLabel,
  keyTypeLabel,
  occupancyLabel,
  getCancelMessage,
  getCancelReasonOptions,
  republishAlertMessage,
  visitIntentionLabel,
} from '../utils/visitFlowCopy';
import {
  latestPostVisitReportVersion,
  postVisitArtifactFrom,
  postVisitArtifactState,
  postVisitDiffRows,
  postVisitReportVersions,
  postVisitVersionLabel,
} from '../utils/postVisitReport';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const store = useVisitasStore();

const isLoading = ref(true);
const loadError = ref('');
const isConfirming = ref(false);
const isCanceling = ref(false);
const showCancelModal = ref(false);
const cancelForm = reactive({
  responsible: 'demand',
  reason: 'client_stopped_responding',
  notes: '',
  suspendListing: false,
  suspensionStartsAt: '',
  suspensionEndsAt: '',
});

const visita = computed(() =>
  camelcaseKeys(
    store.records.find(record => record.id === Number(route.params.id)) || {},
    {
      deep: true,
    }
  )
);

const reviewPayloadIssues = computed(() => {
  const issues = [];

  if (!visita.value.id) issues.push('Identificador da visita ausente.');
  if (!visita.value.scheduledAt) issues.push('Data e horário ausentes.');
  if (!(visita.value.contact?.id || visita.value.contactId)) {
    issues.push('Cliente da visita ausente.');
  }
  if (!(visita.value.imovel?.id || visita.value.imovelId)) {
    issues.push('Imóvel da visita ausente.');
  }

  return issues;
});
const isReviewPayloadValid = computed(
  () => reviewPayloadIssues.value.length === 0
);

const currentAccount = computed(() => {
  const accounts = currentUser.value?.accounts || [];
  return accounts.find(account => account.id === accountId.value) || {};
});
const agentName = computed(
  () => currentUser.value?.name || currentUser.value?.email || 'Imudei'
);
const companyName = computed(
  () =>
    currentAccount.value?.name || currentAccount.value?.account_name || 'Imudei'
);

const canConfirm = computed(
  () => isReviewPayloadValid.value && ['agendada'].includes(visita.value.status)
);
const canCancel = computed(
  () =>
    isReviewPayloadValid.value &&
    !['cancelada', 'realizada', 'nao_compareceu', 'nao_sucedida'].includes(
      visita.value.status
    )
);
const canFinish = computed(
  () =>
    isReviewPayloadValid.value && !['cancelada'].includes(visita.value.status)
);

const statusTone = computed(() => {
  if (['confirmada', 'realizada'].includes(visita.value.status)) {
    return 'text-emerald-700 bg-emerald-50';
  }

  if (
    ['cancelada', 'nao_compareceu', 'nao_sucedida'].includes(
      visita.value.status
    )
  ) {
    return 'text-rose-700 bg-rose-50';
  }

  return 'text-sky-700 bg-sky-50';
});

const statusLabel = computed(() => {
  return (
    {
      agendada: 'Visita agendada',
      confirmada: 'Visita confirmada',
      realizada: 'Visita realizada',
      cancelada: 'Visita cancelada',
      nao_compareceu: 'Visita não realizada',
      nao_sucedida: 'Visita não sucedida',
    }[visita.value.status] ||
    visita.value.status ||
    '—'
  );
});

const formatDateTime = value => {
  if (!value) return '—';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(new Date(value));
};

const visitAddress = computed(() =>
  [
    visita.value.imovel?.endereco,
    visita.value.imovel?.bairro,
    visita.value.imovel?.cidade,
  ]
    .filter(Boolean)
    .join(', ')
);

const entryAccessDetails = computed(
  () =>
    visita.value.additionalAttributes?.entryAccess ||
    visita.value.additionalAttributes?.entry_access ||
    {}
);
const entryAuthorization = computed(() =>
  entryAccessLabel(
    entryAccessDetails.value.accessLabel ||
      entryAccessDetails.value.access_label ||
      entryAccessDetails.value.accessKey ||
      entryAccessDetails.value.access_key ||
      visita.value.additionalAttributes?.entryAuthorization ||
      visita.value.additionalAttributes?.entry_authorization ||
      visita.value.additionalAttributes?.entradaNoImovel ||
      visita.value.additionalAttributes?.entrada_no_imovel
  )
);
const entryAccessRows = computed(() =>
  [
    entryAccessDetails.value.doormanLabel ||
    entryAccessDetails.value.doorman_label ||
    entryAccessDetails.value.doormanType ||
    entryAccessDetails.value.doorman_type
      ? {
          label: 'Qual o horário da portaria?',
          value:
            entryAccessDetails.value.doormanLabel ||
            entryAccessDetails.value.doorman_label ||
            doormanLabel(
              entryAccessDetails.value.doormanType ||
                entryAccessDetails.value.doorman_type
            ),
        }
      : null,
    entryAccessDetails.value.houseOccupancyLabel ||
    entryAccessDetails.value.house_occupancy_label ||
    entryAccessDetails.value.houseOccupancy ||
    entryAccessDetails.value.house_occupancy
      ? {
          label: 'Alguém mora no imóvel?',
          value:
            entryAccessDetails.value.houseOccupancyLabel ||
            entryAccessDetails.value.house_occupancy_label ||
            occupancyLabel(
              entryAccessDetails.value.houseOccupancy ||
                entryAccessDetails.value.house_occupancy
            ),
        }
      : null,
    entryAccessDetails.value.keyTypeLabel ||
    entryAccessDetails.value.key_type_label ||
    entryAccessDetails.value.keyType ||
    entryAccessDetails.value.key_type
      ? {
          label: 'Qual o tipo de chave?',
          value:
            entryAccessDetails.value.keyTypeLabel ||
            entryAccessDetails.value.key_type_label ||
            keyTypeLabel(
              entryAccessDetails.value.keyType ||
                entryAccessDetails.value.key_type
            ),
        }
      : null,
    entryAccessDetails.value.responsibleName ||
    entryAccessDetails.value.responsible_name
      ? {
          label: 'Nome do responsável',
          value:
            entryAccessDetails.value.responsibleName ||
            entryAccessDetails.value.responsible_name,
        }
      : null,
    entryAccessDetails.value.responsiblePhone ||
    entryAccessDetails.value.responsible_phone
      ? {
          label: 'Telefone de contato',
          value:
            entryAccessDetails.value.responsiblePhone ||
            entryAccessDetails.value.responsible_phone,
        }
      : null,
    entryAccessDetails.value.responsibleAddress ||
    entryAccessDetails.value.responsible_address
      ? {
          label: 'Endereço do responsável',
          value:
            entryAccessDetails.value.responsibleAddress ||
            entryAccessDetails.value.responsible_address,
        }
      : null,
    entryAccessDetails.value.instructions
      ? {
          label: 'Instruções de acesso',
          value: entryAccessDetails.value.instructions,
        }
      : null,
  ].filter(Boolean)
);
const visitHistory = computed(
  () =>
    visita.value.imovel?.visitHistory ||
    visita.value.imovel?.visit_history ||
    visita.value.additionalAttributes?.visitHistory ||
    visita.value.additionalAttributes?.visit_history ||
    []
);
const ownerPresent = computed(() =>
  Boolean(
    entryAccessDetails.value.ownerPresent ||
      entryAccessDetails.value.owner_present ||
      visita.value.additionalAttributes?.ownerPresent ||
      visita.value.additionalAttributes?.owner_present
  )
);

const visitIntention = computed(() =>
  visitIntentionLabel(visita.value.buyerVisitIntention || 'indefinido')
);

const cancellationDetails = computed(
  () =>
    visita.value.additionalAttributes?.visitCancellation ||
    visita.value.additionalAttributes?.visit_cancellation ||
    null
);

const cancellationReason = computed(
  () =>
    cancellationDetails.value?.reasonMessage ||
    cancellationDetails.value?.reason_message ||
    ''
);

const cancellationResponsible = computed(
  () =>
    cancellationDetails.value?.responsibleLabel ||
    cancellationDetails.value?.responsible_label ||
    cancelResponsibleLabel(cancellationDetails.value?.responsible)
);

const cancellationSuspension = computed(
  () =>
    cancellationDetails.value?.suspensionPeriod ||
    cancellationDetails.value?.suspension_period ||
    null
);

const cancellationRepublishMessage = computed(
  () =>
    cancellationDetails.value?.republishMessage ||
    cancellationDetails.value?.republish_message ||
    ''
);

const canSuspendListing = computed(
  () =>
    currentAccount.value?.features?.listing_suspension !== false &&
    currentAccount.value?.settings?.can_suspend_listing !== false
);

const cancelReasonOptions = computed(() =>
  getCancelReasonOptions(cancelForm.responsible, {
    canSuspendListing: canSuspendListing.value,
  })
);

const cancelReasonHelperMessage = computed(() =>
  getCancelMessage(cancelForm.reason, {
    canSuspendListing: canSuspendListing.value,
  })
);

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return isoDate(date);
}

function formattedDate(value) {
  if (!value) return '—';

  return new Intl.DateTimeFormat('pt-BR').format(new Date(value));
}

const suspensionAlert = computed(() =>
  cancelForm.suspendListing && cancelForm.suspensionEndsAt
    ? republishAlertMessage(formattedDate(cancelForm.suspensionEndsAt))
    : ''
);
const cancelConfirmLabel = computed(() =>
  cancelForm.suspendListing
    ? VISIT_CANCEL_SUSPEND_CONFIRM_LABEL
    : VISIT_CANCEL_CONFIRM_LABEL
);
const cancelSuccessTitle = computed(() =>
  cancelForm.suspendListing
    ? VISIT_CANCEL_SUSPEND_SUCCESS_TITLE
    : VISIT_CANCEL_SUCCESS_TITLE
);
const cancelSuccessMessage = computed(() =>
  cancelForm.suspendListing
    ? VISIT_CANCEL_SUSPEND_SUCCESS_MESSAGE
    : VISIT_CANCEL_SUCCESS_MESSAGE
);
const visitSummary = computed(() => ({
  address: visitAddress.value || null,
  client: visita.value.contact?.name || null,
  date: formatDateTime(visita.value.scheduledAt),
  agent: visita.value.user?.name || null,
  imovel_id: visita.value.imovel?.id || visita.value.imovelId,
  imovel_title: visita.value.imovel?.titulo || null,
}));
const invalidReviewPayloadMessage =
  'Complete os dados obrigatórios antes de confirmar ou finalizar a visita.';
const postVisitArtifact = computed(() => postVisitArtifactFrom(visita.value));
const postVisitState = computed(() => postVisitArtifactState(visita.value));
const latestReportVersion = computed(() =>
  latestPostVisitReportVersion(postVisitArtifact.value)
);
const reportVersions = computed(() =>
  postVisitReportVersions(postVisitArtifact.value)
);
const latestReportDiffRows = computed(() =>
  postVisitDiffRows(latestReportVersion.value)
);
const postVisitStateLabel = computed(
  () =>
    ({
      done: 'Resultado realizado',
      not_done: 'Resultado não realizado',
      no_show: 'Não compareceu',
      review: 'Em revisão',
      loading: 'Carregando',
      empty: 'Sem laudo versionado',
    })[postVisitState.value] || 'Em revisão'
);

const ensureValidReviewPayload = () => {
  if (isReviewPayloadValid.value) return true;

  useAlert(invalidReviewPayloadMessage);
  return false;
};

const resetCancelForm = () => {
  cancelForm.responsible = 'demand';
  cancelForm.reason = 'client_stopped_responding';
  cancelForm.notes = '';
  cancelForm.suspendListing = false;
  cancelForm.suspensionStartsAt = isoDate(new Date());
  cancelForm.suspensionEndsAt = addDays(7);
};

const updateCancelResponsible = () => {
  cancelForm.reason = cancelReasonOptions.value[0]?.value || '';
};

const openCancelModal = () => {
  resetCancelForm();
  showCancelModal.value = true;
};

function buildCancellationAttributes() {
  return {
    visit_cancellation: {
      title: VISIT_CANCEL_DETAILS_TITLE,
      responsible: cancelForm.responsible,
      responsible_label: cancelResponsibleLabel(cancelForm.responsible),
      reason: cancelForm.reason,
      reason_message: cancelReasonLabel(
        cancelForm.responsible,
        cancelForm.reason
      ),
      reason_helper_message: cancelReasonHelperMessage.value || null,
      notes: cancelForm.notes?.trim() || null,
      confirm_title: VISIT_CANCEL_CONFIRM_TITLE,
      confirm_message: VISIT_CANCELLATION_NOTICE,
      confirm_label: cancelConfirmLabel.value,
      reschedule_suggested: true,
      visit_summary: visitSummary.value,
      suspend_listing: cancelForm.suspendListing,
      suspension_period: cancelForm.suspendListing
        ? {
            starts_at: cancelForm.suspensionStartsAt,
            ends_at: cancelForm.suspensionEndsAt,
            label: VISIT_CANCEL_SUSPEND_LABEL,
            helper: VISIT_CANCEL_SUSPEND_HELPER,
          }
        : null,
      republish_message: suspensionAlert.value || null,
      success_title: cancelSuccessTitle.value,
      success_message: cancelSuccessMessage.value,
      canceled_at: new Date().toISOString(),
    },
  };
}

const validateSuspensionPeriod = () => {
  if (!cancelForm.suspendListing) return true;

  if (!cancelForm.suspensionStartsAt || !cancelForm.suspensionEndsAt) {
    useAlert(VISIT_CANCEL_INVALID_DATE_MESSAGE);
    return false;
  }

  const startsAt = new Date(cancelForm.suspensionStartsAt);
  const endsAt = new Date(cancelForm.suspensionEndsAt);

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    useAlert(VISIT_CANCEL_INVALID_DATE_MESSAGE);
    return false;
  }

  if (startsAt > endsAt) {
    useAlert(VISIT_CANCEL_INVALID_FIRST_DAY_MESSAGE);
    return false;
  }

  return true;
};

const carregar = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    await store.show(route.params.id);
  } catch {
    loadError.value = t('VISITS.ERRORS.LOAD_VISIT_DETAILS');
  } finally {
    isLoading.value = false;
  }
};

const voltar = () =>
  router.push(frontendURL(`accounts/${accountId.value}/visitas`));

const editar = () =>
  router.push(
    frontendURL(`accounts/${accountId.value}/visitas/${route.params.id}/edit`)
  );

const finalizar = outcome =>
  ensureValidReviewPayload() &&
  router.push(
    frontendURL(
      `accounts/${accountId.value}/visitas/${route.params.id}/finalizar?outcome=${outcome}`
    )
  );

const abrirAtendimento = () => {
  if (!visita.value.conversation?.id) return;

  router.push(
    frontendURL(
      `accounts/${accountId.value}/conversations/${visita.value.conversation.id}`
    )
  );
};

const confirmar = async () => {
  if (!ensureValidReviewPayload()) return;

  isConfirming.value = true;
  try {
    await store.update({ id: route.params.id, status: 'confirmada' });
    useAlert('Pronto, visita confirmada!');
  } finally {
    isConfirming.value = false;
  }
};

const cancelar = async () => {
  if (!ensureValidReviewPayload()) return;
  if (!cancelForm.reason) {
    useAlert('Informe o motivo de cancelamento da visita');
    return;
  }
  if (!validateSuspensionPeriod()) return;

  isCanceling.value = true;
  try {
    await store.update({
      id: route.params.id,
      status: 'cancelada',
      additional_attributes: buildCancellationAttributes(),
    });
    useAlert(cancelSuccessTitle.value);
    showCancelModal.value = false;
  } finally {
    isCanceling.value = false;
  }
};

onMounted(carregar);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg-page font-redHatText md:overflow-hidden"
  >
    <LaisHeader
      title="Revisar visita"
      helper="Confirme os detalhes do agendamento antes de seguir com o atendimento."
      :breadcrumb="[
        { label: 'Visitas', to: { name: 'visitas_index' } },
        { label: 'Revisar' },
      ]"
    >
      <template #actions>
        <LaisPrimaryButton variant="ghost" @click="voltar">
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-pencil"
          @click="editar"
        >
          Editar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="canConfirm"
          icon="i-lucide-circle-check"
          :is-loading="isConfirming"
          @click="confirmar"
        >
          Confirmar visita
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div
      class="min-h-0 flex-1 overflow-auto max-md:flex-none max-md:overflow-visible"
    >
      <div
        v-if="isLoading"
        class="py-16 text-center text-[13px] text-lais-text-muted"
      >
        {{ t('VISITS.COMMON.LOADING') }}
      </div>

      <div
        v-else-if="loadError"
        class="mx-auto grid max-w-xl gap-4 px-4 py-16 text-center sm:px-6"
      >
        <span
          class="i-lucide-alert-circle mx-auto size-10 text-rose-600"
          aria-hidden="true"
        />
        <div class="grid gap-1">
          <h2 class="text-[18px] font-semibold text-lais-text">
            {{ t('VISITS.ERRORS.LOAD_TITLE') }}
          </h2>
          <p class="text-[14px] leading-relaxed text-lais-text-muted">
            {{ loadError }}
          </p>
        </div>
        <div class="flex justify-center">
          <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="carregar">
            {{ t('VISITS.COMMON.RETRY') }}
          </LaisPrimaryButton>
        </div>
      </div>

      <div
        v-else
        class="grid w-full grid-cols-1 gap-5 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,2fr)_320px] lg:px-10"
      >
        <div class="grid gap-5">
          <LaisCard
            v-if="!isReviewPayloadValid"
            title="Dados obrigatórios ausentes"
            data-test="visita-review-invalid-payload"
          >
            <div
              class="grid gap-3 text-[13px] leading-relaxed text-lais-text-body"
            >
              <p>{{ invalidReviewPayloadMessage }}</p>
              <ul class="list-disc space-y-1 pl-5">
                <li v-for="issue in reviewPayloadIssues" :key="issue">
                  {{ issue }}
                </li>
              </ul>
            </div>
          </LaisCard>

          <LaisCard title="Resumo do agendamento">
            <div class="grid gap-3">
              <LaisInfoRow
                icon="i-lucide-calendar-days"
                label="Quando"
                :value="formatDateTime(visita.scheduledAt)"
              />
              <LaisInfoRow
                icon="i-lucide-building"
                label="Imóvel"
                :value="visita.imovel?.titulo || '—'"
              />
              <LaisInfoRow
                icon="i-lucide-user-round"
                label="Cliente"
                :value="visita.contact?.name || '—'"
              />
              <LaisInfoRow
                icon="i-lucide-badge-check"
                label="Corretor"
                :value="visita.user?.name || 'A designar'"
              />
              <LaisInfoRow
                icon="i-lucide-map-pinned"
                label="Tipo de visita"
                :value="visitIntention"
              />
              <LaisInfoRow
                icon="i-lucide-key-round"
                label="Entrada no imóvel"
                :value="entryAuthorization"
              />
              <div
                v-if="entryAccessRows.length"
                class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3"
              >
                <dl class="grid gap-2 text-[13px] text-lais-text-body">
                  <div
                    v-for="row in entryAccessRows"
                    :key="row.label"
                    class="grid gap-0.5"
                  >
                    <dt class="font-semibold text-lais-text">
                      {{ row.label }}
                    </dt>
                    <dd>{{ row.value }}</dd>
                  </div>
                </dl>
              </div>
              <LaisInfoRow
                icon="i-lucide-message-square-text"
                label="Atendimento"
                :value="
                  visita.conversation?.id
                    ? `Conversa #${visita.conversation.id}`
                    : 'Sem conversa vinculada'
                "
              />
            </div>
          </LaisCard>

          <OccupiedHouseNotice
            :visit="visita"
            :imovel="visita.imovel"
            :agent-name="agentName"
          />

          <HouseContactsList
            :visit="visita"
            :imovel="visita.imovel"
            :account-id="accountId"
            :agent-name="agentName"
            :company-name="companyName"
          />

          <VisitHistorySummary
            :visits="visitHistory"
            :owner-present="ownerPresent"
          />

          <LaisCard
            v-if="latestReportVersion"
            title="Laudo pós-visita versionado"
            data-test="visita-review-report-version"
          >
            <div class="grid gap-4">
              <div
                class="flex flex-col gap-3 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] text-lais-text-body sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p class="font-semibold text-lais-text">
                    {{ postVisitVersionLabel(latestReportVersion) }}
                  </p>
                  <p class="mt-1 text-lais-text-muted">
                    {{ postVisitStateLabel }} -
                    {{
                      formatDateTime(
                        latestReportVersion.submittedAt ||
                          latestReportVersion.submitted_at
                      )
                    }}
                  </p>
                </div>
                <span
                  class="inline-flex w-fit rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-lais-primary"
                >
                  {{ reportVersions.length }} versões
                </span>
              </div>

              <div v-if="latestReportDiffRows.length" class="grid gap-2">
                <p
                  class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
                >
                  Diff redigido da revisão
                </p>
                <div
                  v-for="row in latestReportDiffRows"
                  :key="row.path"
                  class="grid gap-1 rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[13px] text-lais-text-body"
                >
                  <p class="font-semibold text-lais-text">{{ row.path }}</p>
                  <p class="text-lais-text-muted">
                    Antes: {{ row.before }} - Depois: {{ row.after }}
                  </p>
                </div>
              </div>

              <p
                v-else
                class="rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[13px] text-lais-text-muted"
              >
                Primeira versão registrada. A próxima revisão exibirá o diff
                redigido neste bloco.
              </p>
            </div>
          </LaisCard>

          <LaisCard title="Detalhes operacionais">
            <div class="grid gap-4">
              <div>
                <p
                  class="mb-1 text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
                >
                  Observações
                </p>
                <p
                  class="whitespace-pre-wrap text-[14px] leading-relaxed text-lais-text-body"
                >
                  {{ visita.observacoes || 'Nenhuma observação registrada.' }}
                </p>
              </div>
              <div>
                <p
                  class="mb-1 text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
                >
                  Resultado
                </p>
                <p
                  class="whitespace-pre-wrap text-[14px] leading-relaxed text-lais-text-body"
                >
                  {{ visita.feedback || 'Ainda sem resultado registrado.' }}
                </p>
              </div>
              <div v-if="cancellationReason">
                <p
                  class="mb-1 text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
                >
                  Motivo de cancelamento
                </p>
                <p
                  class="whitespace-pre-wrap text-[14px] leading-relaxed text-lais-text-body"
                >
                  <strong v-if="cancellationResponsible" class="font-semibold">
                    {{ cancellationResponsible }}:
                  </strong>
                  {{ cancellationReason }}
                </p>
              </div>
              <div
                v-if="cancellationSuspension || cancellationRepublishMessage"
              >
                <p
                  class="mb-1 text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
                >
                  Suspensão do anúncio
                </p>
                <p
                  class="whitespace-pre-wrap text-[14px] leading-relaxed text-lais-text-body"
                >
                  {{
                    cancellationRepublishMessage ||
                    `Suspender anúncio até ${formattedDate(cancellationSuspension?.endsAt || cancellationSuspension?.ends_at)}`
                  }}
                </p>
              </div>
              <div
                v-if="visita.additionalAttributes?.autoLinkedConversation"
                class="rounded-lais-sm border border-lais-border bg-lais-primary-50/30 px-4 py-3 text-[13px] text-lais-text-body"
              >
                Esta visita foi vinculada automaticamente ao atendimento mais
                recente do cliente.
              </div>
            </div>
          </LaisCard>
        </div>

        <aside class="grid gap-5">
          <LaisCard title="Status atual" density="tight">
            <div class="grid gap-3">
              <span
                class="inline-flex w-fit rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide"
                :class="statusTone"
              >
                {{ statusLabel }}
              </span>
              <p class="text-[13px] leading-relaxed text-lais-text-helper">
                Use esta tela para revisar o agendamento antes de confirmar com
                o cliente e o corretor.
              </p>
              <p
                v-if="cancellationReason"
                class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 py-2 text-[12px] leading-relaxed text-lais-text-body"
              >
                <strong class="font-semibold">Motivo de cancelamento:</strong>
                "{{ cancellationReason }}"
              </p>
              <p
                v-if="cancellationRepublishMessage"
                class="rounded-lais-sm border border-lais-gold/40 bg-lais-gold/10 px-3 py-2 text-[12px] leading-relaxed text-lais-text"
              >
                {{ cancellationRepublishMessage }}
              </p>
            </div>
          </LaisCard>

          <LaisCard title="Ações rápidas" density="tight">
            <div class="grid gap-3">
              <LaisPrimaryButton
                v-if="canConfirm"
                icon="i-lucide-circle-check-big"
                data-test="visita-review-confirm"
                :is-loading="isConfirming"
                @click="confirmar"
              >
                Confirmar agora
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canFinish"
                icon="i-lucide-flag"
                data-test="visita-review-finish-done"
                @click="finalizar('done')"
              >
                Visita realizada
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canFinish"
                variant="ghost"
                icon="i-lucide-circle-x"
                data-test="visita-review-finish-not-done"
                @click="finalizar('not_done')"
              >
                Visita não realizada
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canFinish"
                variant="ghost"
                icon="i-lucide-user-x"
                data-test="visita-review-finish-no-show"
                @click="finalizar('no_show')"
              >
                Não compareceu
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-pencil"
                @click="editar"
              >
                Ajustar agendamento
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="visita.conversation?.id"
                variant="ghost"
                icon="i-lucide-messages-square"
                @click="abrirAtendimento"
              >
                Abrir atendimento
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="canCancel"
                variant="ghost"
                icon="i-lucide-ban"
                :is-loading="isCanceling"
                @click="openCancelModal"
              >
                Cancelar visita
              </LaisPrimaryButton>
            </div>
          </LaisCard>
        </aside>
      </div>
    </div>

    <LaisModal
      :show="showCancelModal"
      title="Cancelar visita"
      max-width-class="max-w-[600px]"
      @close="showCancelModal = false"
    >
      <div class="grid gap-4">
        <div
          class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
        >
          <h3 class="text-[15px] font-semibold text-lais-text">
            {{ VISIT_CANCEL_CONFIRM_TITLE }}
          </h3>
          <p class="mt-1 text-lais-text-helper">
            {{ VISIT_CANCELLATION_NOTICE }}
          </p>
        </div>

        <div
          class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3"
        >
          <h3 class="text-[14px] font-semibold text-lais-text">
            {{ VISIT_CANCEL_DETAILS_TITLE }}
          </h3>
          <dl class="mt-3 grid gap-2 text-[13px] text-lais-text-body">
            <div class="grid gap-0.5">
              <dt class="font-semibold text-lais-text">Endereço:</dt>
              <dd>{{ visitAddress || '—' }}</dd>
            </div>
            <div class="grid gap-0.5">
              <dt class="font-semibold text-lais-text">Cliente:</dt>
              <dd>{{ visita.contact?.name || '—' }}</dd>
            </div>
            <div class="grid gap-0.5">
              <dt class="font-semibold text-lais-text">Data:</dt>
              <dd>{{ formatDateTime(visita.scheduledAt) }}</dd>
            </div>
          </dl>
        </div>

        <div
          class="flex flex-col gap-2 rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[13px] text-lais-text-body sm:flex-row sm:items-center sm:justify-between"
        >
          <span>{{ VISIT_CANCEL_RESCHEDULE_TEXT }}</span>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-full border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
            @click="editar"
          >
            <span class="i-lucide-calendar-clock size-3.5" aria-hidden="true" />
            {{ VISIT_CANCEL_RESCHEDULE_LINK }}
          </button>
        </div>

        <div class="grid gap-3">
          <p class="text-[14px] font-semibold text-lais-text">
            {{ VISIT_CANCEL_REASON_TITLE }}
          </p>

          <label class="grid gap-1">
            <span
              class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
            >
              Responsável pelo cancelamento
            </span>
            <select
              v-model="cancelForm.responsible"
              class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
              @change="updateCancelResponsible"
            >
              <option
                v-for="option in VISIT_CANCEL_RESPONSIBLE_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>

          <label class="grid gap-1">
            <span
              class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
            >
              Motivo de cancelamento
            </span>
            <select
              v-model="cancelForm.reason"
              class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
            >
              <option
                v-for="option in cancelReasonOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>

          <p
            class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 py-2 text-[13px] text-lais-text-body"
          >
            {{ VISIT_CANCELLATION_NOTICE }}
          </p>

          <p
            class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 py-2 text-[13px] text-lais-text-body"
          >
            <strong class="font-semibold">Motivo de cancelamento:</strong>
            "{{ cancelReasonLabel(cancelForm.responsible, cancelForm.reason) }}"
          </p>

          <p
            v-if="cancelReasonHelperMessage"
            class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 py-2 text-[13px] text-lais-text-body"
          >
            {{ cancelReasonHelperMessage }}
          </p>

          <label class="grid gap-1">
            <span
              class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
            >
              Observação
            </span>
            <textarea
              v-model="cancelForm.notes"
              rows="3"
              class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
              placeholder="Detalhe o contexto quando necessário"
            />
          </label>

          <label
            v-if="canSuspendListing"
            class="flex items-start gap-3 rounded-lais border border-lais-border bg-white px-3 py-3 text-[13px] text-lais-text"
          >
            <input
              v-model="cancelForm.suspendListing"
              type="checkbox"
              class="mt-0.5 size-4 accent-lais-primary"
            />
            <span>
              <strong class="block">{{ VISIT_CANCEL_SUSPEND_TITLE }}</strong>
              <span class="mt-2 block font-semibold text-lais-primary">
                {{ VISIT_CANCEL_SUSPEND_LABEL }}
              </span>
              <span class="mt-1 block text-lais-text-muted">
                {{ VISIT_CANCEL_SUSPEND_HELPER }}
              </span>
            </span>
          </label>

          <div
            v-if="cancelForm.suspendListing"
            class="grid gap-3 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] text-lais-text-body"
          >
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-pause-circle mt-0.5 size-5 shrink-0 text-lais-primary"
                aria-hidden="true"
              />
              <div class="min-w-0">
                <h3 class="text-[14px] font-semibold text-lais-text">
                  {{ VISIT_CANCEL_SUSPEND_FLOW_TITLE }}
                </h3>
                <p class="mt-1 leading-relaxed">
                  {{ VISIT_CANCEL_SUSPEND_FLOW_BODY }}
                </p>
                <p class="mt-2 leading-relaxed text-lais-text-helper">
                  {{ VISIT_CANCEL_SUSPEND_BODY }}
                </p>
              </div>
            </div>
            <p class="font-semibold text-lais-text">
              {{ VISIT_CANCEL_SUSPEND_PERIOD_LABEL }}
            </p>
            <div class="grid gap-3 sm:grid-cols-2">
              <label class="grid gap-1">
                <span
                  class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
                >
                  De
                </span>
                <input
                  v-model="cancelForm.suspensionStartsAt"
                  type="date"
                  class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
              <label class="grid gap-1">
                <span
                  class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
                >
                  {{ VISIT_CANCEL_SUSPEND_LAST_DAY_LABEL }}
                </span>
                <input
                  v-model="cancelForm.suspensionEndsAt"
                  type="date"
                  class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
            </div>
            <p
              v-if="suspensionAlert"
              class="rounded-lais-sm border border-lais-gold/40 bg-lais-gold/10 px-3 py-2 font-semibold text-lais-text"
            >
              {{ suspensionAlert }}
            </p>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <LaisPrimaryButton variant="ghost" @click="showCancelModal = false">
            Voltar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="primary"
            icon="i-lucide-ban"
            :is-loading="isCanceling"
            @click="cancelar"
          >
            {{ cancelConfirmLabel }}
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>
  </section>
</template>
